import { z } from "zod";
import { bilingualStringSchema } from "./bilingual.ts";

/**
 * Treatment packages & sessions API contracts.
 *
 * Money values come back from Postgres `numeric` as strings; we keep them
 * as strings in API types (same convention as invoices/services) and parse
 * at the edge where needed.
 */

export const packageStatusSchema = z.enum(["active", "completed", "cancelled"]);
export type PackageStatus = z.infer<typeof packageStatusSchema>;

export const treatmentTypeSchema = z.enum([
	"general",
	"laser_hair_removal",
	"laser_resurfacing",
	"ipl",
	"other",
]);
export type TreatmentType = z.infer<typeof treatmentTypeSchema>;

export const bodyAreaSchema = z.enum([
	"face",
	"upper_lip",
	"chin",
	"cheeks",
	"neck",
	"underarm",
	"arms",
	"hands",
	"chest",
	"back",
	"abdomen",
	"bikini",
	"brazilian",
	"legs",
	"full_legs",
	"full_body",
	"other",
]);
export type BodyArea = z.infer<typeof bodyAreaSchema>;

// ===== Catalog (treatment_packages) =====

export const treatmentPackageApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: bilingualStringSchema,
	description: z
		.object({ ar: z.string().optional(), en: z.string().optional() })
		.nullable(),
	total_sessions: z.number().int().positive(),
	price: z.string(),
	currency: z.string(),
	validity_days: z.number().int().positive().nullable(),
	treatment_type: treatmentTypeSchema,
	body_area: bodyAreaSchema.nullable(),
	is_active: z.boolean(),
	sort_order: z.number().int(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type TreatmentPackageApi = z.infer<typeof treatmentPackageApiSchema>;

export const createPackageInputSchema = z.object({
	name: bilingualStringSchema,
	description: z
		.object({ ar: z.string().optional(), en: z.string().optional() })
		.optional()
		.nullable(),
	total_sessions: z.number().int().positive().max(100).default(1),
	price: z.coerce.number().nonnegative().default(0),
	currency: z.string().default("IQD"),
	validity_days: z.number().int().positive().max(3650).nullable().optional(),
	treatment_type: treatmentTypeSchema.default("general"),
	body_area: bodyAreaSchema.nullable().optional(),
	is_active: z.boolean().default(true),
	sort_order: z.number().int().default(0),
});
export type CreatePackageInput = z.infer<typeof createPackageInputSchema>;

export const updatePackageInputSchema = createPackageInputSchema.partial();
export type UpdatePackageInput = z.infer<typeof updatePackageInputSchema>;

// ===== Sold instance (patient_packages) =====

export const packageSessionApiSchema = z.object({
	id: z.string().uuid(),
	patient_package_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	performed_by_member_id: z.string().uuid().nullable(),
	performed_by_name: z.string().nullable(),
	session_number: z.number().int().positive(),
	notes: z.string().nullable(),
	// Laser / hair-removal parameters (null for general sessions).
	body_area: bodyAreaSchema.nullable(),
	device_type: z.string().nullable(),
	energy_fluence: z.number().nullable(),
	pulse_duration: z.string().nullable(),
	spot_size: z.string().nullable(),
	shots: z.number().int().nullable(),
	used_at: z.string().datetime(),
});
export type PackageSessionApi = z.infer<typeof packageSessionApiSchema>;

export const patientPackageApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	package_id: z.string().uuid().nullable(),
	name: bilingualStringSchema,
	// Hydrated from the catalog (via package_id) so the redemption UI knows
	// whether to surface laser/hair-removal parameters. Defaults to 'general'.
	treatment_type: treatmentTypeSchema,
	body_area: bodyAreaSchema.nullable(),
	total_sessions: z.number().int().positive(),
	sessions_used: z.number().int().nonnegative(),
	sessions_remaining: z.number().int().nonnegative(),
	price: z.string(),
	currency: z.string(),
	status: packageStatusSchema,
	invoice_id: z.string().uuid().nullable(),
	sold_by_member_id: z.string().uuid().nullable(),
	purchased_at: z.string().datetime(),
	expires_at: z.string().datetime().nullable(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
	// Hydrated on the detail fetch (not the list) — the redemption journal.
	sessions: z.array(packageSessionApiSchema).optional(),
});
export type PatientPackageApi = z.infer<typeof patientPackageApiSchema>;

/**
 * Sell a package to a patient. Either reference a catalog package
 * (`package_id` → snapshot its name/sessions/price) or pass a fully
 * custom one-off (`name` + `total_sessions` + `price`).
 *
 * `create_invoice` routes the sale through the billing engine so the
 * money lands in the same place as every other charge.
 */
export const sellPackageInputSchema = z
	.object({
		patient_id: z.string().uuid(),
		package_id: z.string().uuid().optional(),
		// Custom/override fields (also used to snapshot the catalog values):
		name: bilingualStringSchema.optional(),
		total_sessions: z.number().int().positive().max(100).optional(),
		price: z.coerce.number().nonnegative().optional(),
		currency: z.string().optional(),
		notes: z.string().max(1000).optional(),
		create_invoice: z.boolean().default(true),
	})
	.refine((v) => v.package_id || (v.name && v.total_sessions !== undefined), {
		message: "Either package_id or a custom name + total_sessions is required",
	});
export type SellPackageInput = z.infer<typeof sellPackageInputSchema>;

export const redeemSessionInputSchema = z.object({
	visit_id: z.string().uuid().optional(),
	notes: z.string().max(1000).optional(),
	// Optional laser / hair-removal parameters captured for this session.
	body_area: bodyAreaSchema.optional(),
	device_type: z.string().max(120).optional(),
	energy_fluence: z.number().nonnegative().optional(),
	pulse_duration: z.string().max(60).optional(),
	spot_size: z.string().max(60).optional(),
	shots: z.number().int().nonnegative().optional(),
});
export type RedeemSessionInput = z.infer<typeof redeemSessionInputSchema>;
